import { MediaDimensions } from "@/lib/types/media/core";
import { validateMediaFile } from "@/lib/utils/media-validation";
import { MediaPlacement, MediaType } from "@prisma/client";
import { z } from "zod";

// Schema for media dimensions
export const uploadDimensionsSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
  aspectRatio: z.number().optional(),
  dpi: z.number().optional()
});

// Schema for media metadata
export const uploadMetadataSchema = z.object({
  ownerId: z.string(),
  mimeType: z.string(),
  size: z.number(),
  dimensions: uploadDimensionsSchema.optional(),
  alt: z.string().optional(),
  caption: z.string().optional(),
  originalName: z.string().optional()
});



// Export types
export type UploadDimensionsSchema = z.infer<typeof uploadDimensionsSchema>;
export type UploadMetadataSchema = z.infer<typeof uploadMetadataSchema>;

// Base metadata schema that can be reused
export const mediaMetadataSchema = z.object({
  dimensions: z.object({
    width: z.number().nullable(),
    height: z.number().nullable(),
    aspectRatio: z.number().nullable(),
    dpi: z.number().optional().nullable()
  }).optional(),
  mimeType: z.string().optional(),
  size: z.number().optional(),
  alt: z.string().nullable().default(''),
  ownerId: z.string().default('system'),
  placement: z.nativeEnum(MediaPlacement).default(MediaPlacement.PRODUCT_MEDIA),
  caption: z.string().nullable().default(''),
  originalName: z.string().nullable(),
  orderIndex: z.number().nullable().default(0),
  thumbnailUrl: z.string().optional(),
  duration: z.number().optional(),
  description: z.string().optional(),
  customPath: z.string().optional()
});

// Schema for base media properties
export const baseMediaSchema = z.object({
  id: z.string(),
  url: z.string().min(1, "URL is required")
    .transform(url => {
      // Transform temporary URLs to valid placeholder URLs
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        // Generate a placeholder URL instead of failing validation
        const randomId = Math.random().toString(36).substring(2, 15);
        return `https://placeholder.com/image-${randomId}`;
      }
      return url;
    }),
  placement: z.nativeEnum(MediaPlacement),
  type: z.nativeEnum(MediaType),
  size: z.number().min(1, "Size is required"),
  width: z.number().nullable(),
  height: z.number().nullable(),
  altText: z.string().nullable(),
  caption: z.string().nullable(),
  aspectRatio: z.number().nullable(),
  dpi: z.number().nullable(),
  mimeType: z.string().min(1, "MIME type is required"),
  metadata: mediaMetadataSchema.extend({
    originalName: z.string().nullable().optional()
  })
});

// Schema for media input validation that extends base schema
export const mediaInputSchema = baseMediaSchema.extend({
  uploadProgress: z.number().min(0).max(100).optional(),
  uploadError: z.string().optional(),
  durationSeconds: z.number().nullable().optional()
});

// Schema for fully formed media items
export const mediaItemSchema = baseMediaSchema.extend({
  createdAt: z.string(),
  updatedAt: z.string(),
  status: z.enum(['pending', 'processing', 'ready', 'error', 'deleted']),
  version: z.number().min(1),
  processingComplete: z.boolean(),
  isDeleted: z.boolean()
});

// Update the MediaInputItem type definition
export type MediaInputItem = z.infer<typeof mediaInputSchema>;

// Helper function to validate file before upload using our centralized validation
export async function validateFileForUpload(
  file: File,
  placement: MediaPlacement
): Promise<{
  isValid: boolean;
  error?: string;
  dimensions?: MediaDimensions
}> {
  const result = await validateMediaFile(file, placement);

  if (!result.isValid) {
    return {
      isValid: false,
      error: result.error || 'Validation failed'
    };
  }

  return {
    isValid: true,
    dimensions: result.metadata?.dimensions
  };
}

// Helper function to validate media input against schema
export function validateMediaInput(input: unknown): {
  isValid: boolean;
  error?: string;
  data?: MediaInputItem
} {
  try {
    const validatedData = mediaInputSchema.parse(input);
    return {
      isValid: true,
      data: validatedData
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isValid: false,
        error: error.issues[0].message
      };
    }
    return {
      isValid: false,
      error: 'Invalid media input'
    };
  }
}

// Helper function to get image dimensions
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}
